import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { TrendingUp, Egg, Activity, Wheat, Filter, RefreshCw, FileSpreadsheet, FileText, BarChart3, Calendar, CalendarDays, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { ToggleGroup, ToggleGroupItem } from '../components/ui/toggle-group';
import {
  ResponsiveContainer,
  LineChart, Line,
  BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { toast } from 'sonner';
import { getLotes, getNaves, getAnalisisProduccion } from '../lib/api';
import { formatDate } from '../lib/dateUtils';
import { exportToExcel, exportAnalisisProduccionToPDF } from '../lib/exportUtils';

const COLOR_POSTURA = '#16a34a';      // verde
const COLOR_MORT = '#dc2626';         // rojo
const COLOR_ALIMENTO = '#d97706';     // ámbar/mamey

export default function AnalisisProduccion() {
  const [lotes, setLotes] = useState([]);
  const [naves, setNaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analizando, setAnalizando] = useState(false);

  // Filters — por defecto la semana actual (domingo → sábado).
  // Si hoy es martes 12-5-26 → desde 10-5-26 (dom) hasta 16-5-26 (sáb).
  const _getSemanaActualISO = () => {
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const diaSemana = hoy.getDay(); // 0=Dom .. 6=Sáb
    const dom = new Date(hoy);
    dom.setDate(hoy.getDate() - diaSemana);
    const sab = new Date(dom);
    sab.setDate(dom.getDate() + 6);
    const toISO = (d) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    return { desde: toISO(dom), hasta: toISO(sab) };
  };
  const _semanaActual = _getSemanaActualISO();

  const [filtroLote, setFiltroLote] = useState('all');
  const [filtroNave, setFiltroNave] = useState('all');
  const [fechaDesde, setFechaDesde] = useState(_semanaActual.desde);
  const [fechaHasta, setFechaHasta] = useState(_semanaActual.hasta);
  const [semanaDesde, setSemanaDesde] = useState('');
  const [semanaHasta, setSemanaHasta] = useState('');
  const [vista, setVista] = useState('dia'); // 'dia' | 'semana_edad'

  // Limpia todos los filtros (vuelve a la semana actual)
  const limpiarFiltros = () => {
    const s = _getSemanaActualISO();
    setFiltroLote('all');
    setFiltroNave('all');
    setFechaDesde(s.desde);
    setFechaHasta(s.hasta);
    setSemanaDesde('');
    setSemanaHasta('');
    setVista('dia');
  };

  // Data
  const [resumen, setResumen] = useState(null);
  const [serie, setSerie] = useState([]);

  const loadInitial = useCallback(async () => {
    setLoading(true);
    try {
      const [lotesRes, navesRes] = await Promise.all([getLotes(), getNaves()]);
      setLotes(lotesRes.data || []);
      setNaves(navesRes.data || []);
    } catch (err) {
      toast.error('Error cargando lotes y naves');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadInitial(); }, [loadInitial]);

  // Cuando cambia el lote seleccionado, resetear nave y filtrar las disponibles
  const navesDisponibles = useMemo(() => {
    if (filtroLote === 'all') return naves;
    return naves.filter((n) => n.lote_id === filtroLote);
  }, [filtroLote, naves]);

  useEffect(() => {
    // Si la nave seleccionada no pertenece al lote, resetear
    if (filtroNave !== 'all') {
      const stillValid = navesDisponibles.some((n) => (n.id || n._id) === filtroNave);
      if (!stillValid) setFiltroNave('all');
    }
  }, [filtroLote, navesDisponibles, filtroNave]);

  const aplicarFiltros = useCallback(async () => {
    setAnalizando(true);
    try {
      const params = {};
      if (filtroLote !== 'all') params.lote_id = filtroLote;
      if (filtroNave !== 'all') params.nave_id = filtroNave;
      if (fechaDesde) params.fecha_desde = fechaDesde;
      if (fechaHasta) params.fecha_hasta = fechaHasta;
      if (semanaDesde !== '' && !isNaN(parseInt(semanaDesde))) params.semana_desde = parseInt(semanaDesde);
      if (semanaHasta !== '' && !isNaN(parseInt(semanaHasta))) params.semana_hasta = parseInt(semanaHasta);
      const res = await getAnalisisProduccion(params);
      setResumen(res.data?.resumen || null);
      setSerie(res.data?.serie || []);
    } catch (err) {
      console.error('Error al analizar producción:', err);
      toast.error('Error al analizar producción');
    } finally {
      setAnalizando(false);
    }
  }, [filtroLote, filtroNave, fechaDesde, fechaHasta, semanaDesde, semanaHasta]);

  // Carga automática inicial
  useEffect(() => {
    if (!loading) aplicarFiltros();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // Datos agregados por fecha O por semana de edad (según vista)
  const serieAgregada = useMemo(() => {
    if (!serie.length) return [];
    const grouped = {};
    serie.forEach((r) => {
      let key, label;
      if (vista === 'semana_edad') {
        if (r.semana_edad === null || r.semana_edad === undefined) return;
        key = `S${r.semana_edad}`;
        label = `Sem ${r.semana_edad}`;
      } else {
        key = (r.fecha || '').slice(0, 10);
        label = formatDate(key);
      }
      if (!grouped[key]) {
        grouped[key] = {
          key,
          label,
          orden: vista === 'semana_edad' ? r.semana_edad : key,
          total_huevos: 0,
          mortalidad: 0,
          gallinas_activas_sum: 0,
          gallinas_activas_count: 0,
          alimento_kg: 0,
          alimento_qq: 0,
          mort_acum_pct_sum: 0,
          mort_acum_pct_count: 0,
        };
      }
      grouped[key].total_huevos += r.total_huevos || 0;
      grouped[key].mortalidad += r.mortalidad || 0;
      grouped[key].gallinas_activas_sum += r.gallinas_activas || 0;
      grouped[key].gallinas_activas_count += 1;
      grouped[key].alimento_kg += r.alimento_kg || 0;
      grouped[key].alimento_qq += r.alimento_qq || 0;
      grouped[key].mort_acum_pct_sum += r.mortalidad_acumulada_pct || 0;
      grouped[key].mort_acum_pct_count += 1;
    });
    return Object.values(grouped)
      .map((d) => {
        // En semana de edad: usamos promedio de gallinas; en diario: suma total de gallinas del día
        const gallinasRef = vista === 'semana_edad'
          ? (d.gallinas_activas_count > 0 ? d.gallinas_activas_sum / d.gallinas_activas_count : 0)
          : d.gallinas_activas_sum;
        // Para postura por semana: necesitamos huevos / (gallinas_promedio * dias_de_la_semana)
        // Pero más práctico: usar promedio del % postura observado por registro en esa semana
        const posturaPct = vista === 'semana_edad'
          ? (d.gallinas_activas_count > 0
              ? Number(((d.total_huevos / (d.gallinas_activas_sum || 1)) * 100).toFixed(2))
              : 0)
          : (gallinasRef > 0 ? Number(((d.total_huevos / gallinasRef) * 100).toFixed(2)) : 0);
        return {
          fecha_label: d.label,
          orden: d.orden,
          postura_pct: posturaPct,
          mortalidad_diaria_pct: gallinasRef > 0 ? Number(((d.mortalidad / gallinasRef) * 100).toFixed(4)) : 0,
          mortalidad_acumulada_pct: d.mort_acum_pct_count > 0 ? Number((d.mort_acum_pct_sum / d.mort_acum_pct_count).toFixed(2)) : 0,
          mortalidad: d.mortalidad,
          alimento_kg: Number(d.alimento_kg.toFixed(2)),
          alimento_qq: Number(d.alimento_qq.toFixed(2)),
          total_huevos: d.total_huevos,
        };
      })
      .sort((a, b) => {
        if (typeof a.orden === 'number' && typeof b.orden === 'number') return a.orden - b.orden;
        return String(a.orden).localeCompare(String(b.orden));
      });
  }, [serie, vista]);

  // Detalle por registro: la fecha más reciente debe ir ARRIBA.
  // Dentro de un mismo día, mantenemos las naves ordenadas numéricamente
  // (Nave 1, Nave 2, ...) para que el bloque de cada fecha sea legible.
  const serieDesc = useMemo(() => {
    const naveNum = (nombre) => {
      const m = String(nombre || '').match(/(\d+)/);
      return m ? parseInt(m[1], 10) : 9999;
    };
    return [...serie].sort((a, b) => {
      const fa = String(a.fecha || '').slice(0, 10);
      const fb = String(b.fecha || '').slice(0, 10);
      if (fa !== fb) return fb.localeCompare(fa); // descendente por fecha
      return naveNum(a.nave_nombre) - naveNum(b.nave_nombre); // asc por nave
    });
  }, [serie]);

  const exportarExcel = () => {
    const ok = exportToExcel(
      serieDesc,
      [
        { label: 'Fecha', accessor: (r) => formatDate(r.fecha) },
        { label: 'Nave', key: 'nave_nombre' },
        { label: 'Lote', key: 'lote_nombre' },
        { label: 'Sem. Edad', key: 'semana_edad' },
        { label: 'Total huevos', key: 'total_huevos' },
        { label: 'Gallinas activas', key: 'gallinas_activas' },
        { label: '% Postura', accessor: (r) => `${Number(r.postura_pct || 0).toFixed(2)}%` },
        { label: 'Mortalidad', key: 'mortalidad' },
        { label: '% Mort. diaria', accessor: (r) => `${Number(r.mortalidad_diaria_pct || 0).toFixed(4)}%` },
        { label: '% Mort. acumulada', accessor: (r) => `${Number(r.mortalidad_acumulada_pct || 0).toFixed(2)}%` },
        { label: 'Alimento (kg)', key: 'alimento_kg' },
        { label: 'Alimento (qq)', key: 'alimento_qq' },
      ],
      'analisis_produccion',
      'Analisis'
    );
    if (!ok) toast.error('No hay datos para exportar');
    else toast.success('Excel descargado');
  };

  const imprimirPDF = () => {
    if (!serie || serie.length === 0) {
      toast.error('No hay datos para imprimir');
      return;
    }
    // Resolver labels legibles para el contexto de filtros
    const loteObj = lotes.find((l) => (l.id || l._id) === filtroLote);
    const naveObj = naves.find((n) => (n.id || n._id) === filtroNave);
    const filtros = {
      lote_label: filtroLote === 'all' ? 'Todos los lotes' : (loteObj?.numero_lote || '—'),
      nave_label: filtroNave === 'all'
        ? (filtroLote === 'all' ? 'Todas las naves' : 'Todas las naves del lote')
        : (naveObj?.nombre_nave || '—'),
      fecha_desde: fechaDesde,
      fecha_hasta: fechaHasta,
      semana_desde: semanaDesde,
      semana_hasta: semanaHasta,
      vista,
    };
    const result = exportAnalisisProduccionToPDF({ resumen, serie: serieDesc, filtros }, 'print');
    if (result === 'saved-fallback') {
      toast.warning('Se descargó el PDF (popup bloqueado para imprimir directo)');
    } else if (result) {
      toast.success('Abriendo diálogo de impresión...');
    } else {
      toast.error('No se pudo generar el PDF');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="page-analisis-produccion">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            <BarChart3 className="w-6 h-6" /> Análisis de Producción
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Indicadores clave: % postura, % mortalidad y consumo de alimento por lote o nave.
          </p>
        </div>
        <Button variant="outline" onClick={aplicarFiltros} disabled={analizando} data-testid="btn-refresh-analisis">
          <RefreshCw className={`w-4 h-4 mr-2 ${analizando ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            <Filter className="w-4 h-4" /> Filtros
          </CardTitle>
          <ToggleGroup type="single" value={vista} onValueChange={(v) => v && setVista(v)} data-testid="toggle-vista">
            <ToggleGroupItem value="dia" aria-label="Vista por día" className="gap-1.5 h-8 text-xs">
              <Calendar className="w-3.5 h-3.5" /> Por Día
            </ToggleGroupItem>
            <ToggleGroupItem value="semana_edad" aria-label="Vista por semana de edad" className="gap-1.5 h-8 text-xs">
              <CalendarDays className="w-3.5 h-3.5" /> Por Semana de Edad
            </ToggleGroupItem>
          </ToggleGroup>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div>
              <Label className="text-xs">Lote</Label>
              <Select value={filtroLote} onValueChange={setFiltroLote}>
                <SelectTrigger className="h-10 mt-1.5" data-testid="filtro-lote">
                  <SelectValue placeholder="Todos los lotes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los lotes</SelectItem>
                  {lotes.map((l) => (
                    <SelectItem key={l.id || l._id} value={l.id || l._id}>
                      {l.numero_lote}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Nave</Label>
              <Select value={filtroNave} onValueChange={setFiltroNave}>
                <SelectTrigger className="h-10 mt-1.5" data-testid="filtro-nave">
                  <SelectValue placeholder="Todas las naves" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {filtroLote === 'all' ? 'Todas las naves' : 'Todas las naves del lote'}
                  </SelectItem>
                  {navesDisponibles.map((n) => (
                    <SelectItem key={n.id || n._id} value={n.id || n._id}>
                      {n.nombre_nave}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Desde</Label>
              <Input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} className="h-10 mt-1.5" data-testid="filtro-desde" />
            </div>
            <div>
              <Label className="text-xs">Hasta</Label>
              <Input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} className="h-10 mt-1.5" data-testid="filtro-hasta" />
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={aplicarFiltros} disabled={analizando} className="flex-1" data-testid="btn-aplicar-filtros">
                {analizando ? 'Analizando...' : 'Aplicar'}
              </Button>
              <Button
                onClick={limpiarFiltros}
                variant="outline"
                disabled={analizando}
                title="Limpiar todos los filtros"
                data-testid="btn-limpiar-filtros"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Filtros de semana de edad */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 pt-2 border-t">
            <div className="md:col-span-2 flex items-center gap-2 text-xs text-muted-foreground">
              <CalendarDays className="w-3.5 h-3.5" />
              <span>Filtrar también por semana de edad de las gallinas:</span>
            </div>
            <div>
              <Label className="text-xs">Semana desde</Label>
              <Input
                type="number"
                min="0"
                placeholder="Ej. 18"
                value={semanaDesde}
                onChange={(e) => setSemanaDesde(e.target.value)}
                className="h-10 mt-1.5"
                data-testid="filtro-semana-desde"
              />
            </div>
            <div>
              <Label className="text-xs">Semana hasta</Label>
              <Input
                type="number"
                min="0"
                placeholder="Ej. 80"
                value={semanaHasta}
                onChange={(e) => setSemanaHasta(e.target.value)}
                className="h-10 mt-1.5"
                data-testid="filtro-semana-hasta"
              />
            </div>
            <div className="flex items-end">
              <Button
                variant="ghost"
                size="sm"
                className="w-full h-10"
                onClick={() => { setSemanaDesde(''); setSemanaHasta(''); }}
                data-testid="btn-limpiar-semanas"
              >
                Limpiar semanas
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      {resumen && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="border-l-4 border-l-green-600">
            <CardContent className="p-4">
              <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                <Egg className="w-3 h-3" /> POSTURA PROMEDIO
              </p>
              <p className="text-2xl font-bold tabular-nums mt-1 text-green-700" style={{ fontFamily: 'Space Grotesk' }}>
                {resumen.postura_promedio_pct.toFixed(1)}%
              </p>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {resumen.total_huevos.toLocaleString('en-US')} huevos en {resumen.dias_periodo} días
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-red-600">
            <CardContent className="p-4">
              <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                <Activity className="w-3 h-3" /> MORTALIDAD ACUMULADA
              </p>
              <p className="text-2xl font-bold tabular-nums mt-1 text-red-700" style={{ fontFamily: 'Space Grotesk' }}>
                {resumen.mortalidad_acumulada_pct.toFixed(2)}%
              </p>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {resumen.total_mortalidad.toLocaleString('en-US')} aves de {resumen.gallinas_iniciales.toLocaleString('en-US')}
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-amber-600">
            <CardContent className="p-4">
              <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                <Wheat className="w-3 h-3" /> ALIMENTO CONSUMIDO
              </p>
              <p className="text-2xl font-bold tabular-nums mt-1 text-amber-700" style={{ fontFamily: 'Space Grotesk' }}>
                {resumen.alimento_qq.toFixed(2)} qq
              </p>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {resumen.alimento_kg.toLocaleString('en-US')} kg
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-primary">
            <CardContent className="p-4">
              <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                <TrendingUp className="w-3 h-3" /> POBLACIÓN ACTIVA
              </p>
              <p className="text-2xl font-bold tabular-nums mt-1" style={{ fontFamily: 'Space Grotesk' }}>
                {resumen.gallinas_activas_actual.toLocaleString('en-US')}
              </p>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {resumen.num_naves} nave(s) · {resumen.num_registros} registros
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 gap-6">
        {/* Postura % */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
              <Egg className="w-4 h-4 text-green-700" /> % Postura {vista === 'semana_edad' ? 'por semana de edad' : 'por día'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {serieAgregada.length === 0 ? (
              <div className="h-[260px] flex items-center justify-center text-muted-foreground text-sm">
                Sin datos en el período seleccionado
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={serieAgregada} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="fecha_label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} unit="%" />
                  <Tooltip formatter={(v) => `${v}%`} />
                  <Legend />
                  <Line type="monotone" dataKey="postura_pct" name="% Postura" stroke={COLOR_POSTURA} strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Mortalidad */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
              <Activity className="w-4 h-4 text-red-700" /> Mortalidad — Diaria y Acumulada
            </CardTitle>
          </CardHeader>
          <CardContent>
            {serieAgregada.length === 0 ? (
              <div className="h-[260px] flex items-center justify-center text-muted-foreground text-sm">
                Sin datos en el período seleccionado
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={serieAgregada} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="fecha_label" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} unit="%" />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} unit="%" />
                  <Tooltip formatter={(v) => `${Number(v).toFixed(4)}%`} />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="mortalidad_diaria_pct" name="% Mort. diaria" stroke={COLOR_MORT} strokeWidth={2} dot={{ r: 3 }} />
                  <Line yAxisId="right" type="monotone" dataKey="mortalidad_acumulada_pct" name="% Mort. acumulada" stroke="#9333ea" strokeWidth={2} strokeDasharray="4 4" dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Alimento qq */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
              <Wheat className="w-4 h-4 text-amber-700" /> Consumo de Alimento (qq) {vista === 'semana_edad' ? 'por semana de edad' : 'por día'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {serieAgregada.length === 0 ? (
              <div className="h-[260px] flex items-center justify-center text-muted-foreground text-sm">
                Sin datos en el período seleccionado
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={serieAgregada} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="fecha_label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} unit=" qq" />
                  <Tooltip formatter={(v) => `${v} qq`} />
                  <Legend />
                  <Bar dataKey="alimento_qq" name="Alimento (qq)" fill={COLOR_ALIMENTO} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tabla detallada */}
      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <CardTitle className="text-base" style={{ fontFamily: 'Space Grotesk' }}>
            Detalle por registro ({serie.length})
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={imprimirPDF}
              disabled={serie.length === 0}
              data-testid="btn-pdf-analisis"
            >
              <FileText className="w-4 h-4 mr-2" /> PDF / Imprimir
            </Button>
            <Button variant="outline" size="sm" onClick={exportarExcel} disabled={serie.length === 0} data-testid="btn-export-analisis">
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {serie.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Sin registros en el período seleccionado</p>
            </div>
          ) : (
            <Table
              wrapperClassName="max-h-[calc(100vh-280px)] rounded-md border border-border/40"
              data-testid="tabla-analisis"
            >
                <TableHeader className="sticky top-0 z-30 bg-card shadow-[0_2px_4px_-1px_rgba(0,0,0,0.08)] [&_tr]:border-b-0 [&_th]:border-b [&_th]:border-border">
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Nave</TableHead>
                    <TableHead className="text-center">Sem. Edad</TableHead>
                    <TableHead className="text-right">Huevos</TableHead>
                    <TableHead className="text-right">Gallinas</TableHead>
                    <TableHead className="text-right">% Postura</TableHead>
                    <TableHead className="text-right">Mort.</TableHead>
                    <TableHead className="text-right">% Mort. acum.</TableHead>
                    <TableHead className="text-right">Alimento (qq)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {serieDesc.map((r, idx) => {
                    // Postura óptima ≥ 90%: dorado
                    const isOptimo = r.postura_pct >= 90;
                    const postVar = r.postura_pct >= 80 ? 'default' : (r.postura_pct >= 60 ? 'secondary' : 'destructive');
                    // Detectar cambio de fecha vs la fila anterior
                    const fechaActual = String(r.fecha || '').slice(0, 10);
                    const fechaPrev = idx > 0 ? String(serieDesc[idx - 1].fecha || '').slice(0, 10) : null;
                    const cambioFecha = idx === 0 || fechaActual !== fechaPrev;
                    return (
                      <React.Fragment key={r.id}>
                        {cambioFecha && (
                          <TableRow
                            className="bg-primary/10 hover:bg-primary/15 border-t-2 border-primary/40"
                            data-testid={`divisor-fecha-${fechaActual}`}
                          >
                            <TableCell colSpan={9} className="py-1.5">
                              <div className="flex items-center gap-2 text-xs font-semibold text-primary">
                                <Calendar className="w-3.5 h-3.5" />
                                <span style={{ fontFamily: 'Space Grotesk' }}>
                                  {formatDate(r.fecha)}
                                </span>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                        <TableRow className="hover:bg-accent/60 transition-colors">
                          <TableCell>{formatDate(r.fecha)}</TableCell>
                          <TableCell className="font-medium">{r.nave_nombre}</TableCell>
                          <TableCell className="text-center">
                            {r.semana_edad !== null && r.semana_edad !== undefined ? (
                              <Badge variant="outline" className="text-xs tabular-nums">Sem {r.semana_edad}</Badge>
                            ) : <span className="text-muted-foreground text-xs">—</span>}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">{r.total_huevos.toLocaleString('en-US')}</TableCell>
                          <TableCell className="text-right tabular-nums text-muted-foreground">{r.gallinas_activas.toLocaleString('en-US')}</TableCell>
                          <TableCell className="text-right">
                            {isOptimo ? (
                              <Badge
                                className="text-xs tabular-nums bg-amber-400 text-amber-950 border border-amber-600 hover:bg-amber-400 font-bold shadow-sm"
                                title="Óptimo"
                              >
                                ★ {r.postura_pct.toFixed(1)}%
                              </Badge>
                            ) : (
                              <Badge variant={postVar} className="text-xs tabular-nums">{r.postura_pct.toFixed(1)}%</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {r.mortalidad > 0 ? <Badge variant="destructive" className="text-xs">{r.mortalidad}</Badge> : '0'}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-muted-foreground">{r.mortalidad_acumulada_pct.toFixed(2)}%</TableCell>
                          <TableCell className="text-right tabular-nums">{r.alimento_qq.toFixed(2)} qq</TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
